/**
 * Featured Collections — accordion panels (desktop hover/click) + mobile scroll snap.
 */
const FC_SELECTORS = {
  panel: '[data-featured-collection-panel]',
  trigger: '[data-featured-collection-trigger]',
  track: '[data-featured-collections-track]',
  dot: '[data-featured-collections-dot]',
};

const FC_DESKTOP_MQ = '(min-width: 990px)';

class FeaturedCollectionsComponent extends HTMLElement {
  constructor() {
    super();
    this.panels = [];
    this.dots = [];
    this.track = null;
    this.activeIndex = 0;
    this.desktopMq = null;
    this.isDesktop = false;

    this.onPanelClick = this.onPanelClick.bind(this);
    this.onPanelMouseEnter = this.onPanelMouseEnter.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
  }

  connectedCallback() {
    this.panels = [...this.querySelectorAll(FC_SELECTORS.panel)];
    this.dots = [...this.querySelectorAll(FC_SELECTORS.dot)];
    this.track = this.querySelector(FC_SELECTORS.track);

    if (!this.panels.length) return;

    this.desktopMq = window.matchMedia(FC_DESKTOP_MQ);
    this.isDesktop = this.desktopMq.matches;

    const defaultActive = parseInt(this.dataset.defaultActive, 10);
    this.activeIndex = Number.isNaN(defaultActive) ? 0 : defaultActive;
    this.setActive(this.activeIndex, { scroll: !this.isDesktop, immediate: true });

    this.panels.forEach((panel, index) => {
      const trigger = panel.querySelector(FC_SELECTORS.trigger);
      if (!trigger) return;

      trigger.addEventListener('click', (event) => this.onPanelClick(event, index));
      panel.addEventListener('mouseenter', () => this.onPanelMouseEnter(index));
      panel.addEventListener('focusin', () => this.onPanelMouseEnter(index));
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', this.onDotClick);
    });

    if (this.track) {
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('scrollend', this.onScrollEnd, { passive: true });
    }

    this.desktopMq.addEventListener('change', this.onMediaChange);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }

  disconnectedCallback() {
    if (this.desktopMq) {
      this.desktopMq.removeEventListener('change', this.onMediaChange);
    }
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('scrollend', this.onScrollEnd);
    }
    clearTimeout(this._scrollEndTimer);
  }

  onSectionLoad(event) {
    if (!event.target.contains(this)) return;
    const defaultActive = parseInt(this.dataset.defaultActive, 10);
    this.setActive(Number.isNaN(defaultActive) ? 0 : defaultActive, { scroll: !this.isDesktop });
  }

  onMediaChange(event) {
    this.isDesktop = event.matches;
    if (this.isDesktop && this.track) {
      this.track.scrollLeft = 0;
    }
    this.setActive(this.activeIndex, { scroll: !this.isDesktop });
  }

  onPanelClick(event, index) {
    if (event.target.closest('a')) return;
    event.preventDefault();

    const panel = this.panels[index];
    const url = panel?.dataset.panelUrl;

    if (url) {
      if (this.isDesktop || index === this.activeIndex) {
        this.navigateToPanelUrl(panel, url);
        return;
      }
    }

    this.setActive(index, { scroll: !this.isDesktop });
  }

  navigateToPanelUrl(panel, url) {
    const openInNewTab = panel.dataset.panelUrlNewTab === 'true';
    if (openInNewTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.assign(url);
  }

  onPanelMouseEnter(index) {
    if (!this.isDesktop || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.setActive(index, { scroll: false });
  }

  onDotClick(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.featuredCollectionsDot, 10);
    if (Number.isNaN(index)) return;
    this.setActive(index, { scroll: true });
  }

  onScroll() {
    if (this.isDesktop || !this.track || this._scrollRaf || this._isProgrammaticScroll) return;

    this._scrollRaf = window.requestAnimationFrame(() => {
      this._scrollRaf = null;
      const closest = this.getIndexFromScroll();

      if (closest !== this.activeIndex) {
        this.setActive(closest, { scroll: false });
      }
    });
  }

  onScrollEnd() {
    this._isProgrammaticScroll = false;
    if (this.isDesktop || !this.track) return;

    const closest = this.getIndexFromScroll();
    if (closest !== this.activeIndex) {
      this.setActive(closest, { scroll: false });
    }
  }

  getIndexFromScroll() {
    const scrollLeft = this.track.scrollLeft;
    let activeIndex = 0;
    let minDistance = Infinity;

    this.panels.forEach((panel, index) => {
      const targetScroll = this.getPanelScrollTarget(panel);
      const distance = Math.abs(scrollLeft - targetScroll);

      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  getTrackPaddingLeft() {
    if (!this.track) return 0;
    return parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;
  }

  scrollToPanel(index, immediate = false) {
    const panel = this.panels[index];
    if (!panel || !this.track) return;

    const behavior = immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
    const targetScroll = this.getPanelScrollTarget(panel);

    this._isProgrammaticScroll = true;
    this.track.scrollTo({ left: Math.max(0, targetScroll), behavior });

    if (behavior === 'auto') {
      this._isProgrammaticScroll = false;
      return;
    }

    clearTimeout(this._scrollEndTimer);
    this._scrollEndTimer = window.setTimeout(() => {
      this._isProgrammaticScroll = false;
    }, 500);
  }

  getPanelScrollTarget(panel) {
    const trackRect = this.track.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    return this.track.scrollLeft + panelRect.left - trackRect.left - this.getTrackPaddingLeft();
  }

  setActive(index, options = {}) {
    const { scroll = false, immediate = false } = options;
    if (index < 0 || index >= this.panels.length) return;

    this.activeIndex = index;

    this.panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('is-active', isActive);

      const trigger = panel.querySelector(FC_SELECTORS.trigger);
      if (trigger) {
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }
    });

    this.dots.forEach((dot) => {
      const dotIndex = parseInt(dot.dataset.featuredCollectionsDot, 10);
      dot.classList.toggle('is-active', dotIndex === index);
    });

    if (scroll && this.track && !this.isDesktop) {
      this.scrollToPanel(index, immediate);
    } else if (this.isDesktop && this.track) {
      this.track.scrollLeft = 0;
    }

    this.updatePanelImageDelivery();
  }

  updatePanelImageDelivery() {
    if (!this.isDesktop) return;

    const mobileSizes = 'min(340px, calc(100vw - 48px))';

    this.panels.forEach((panel, i) => {
      const img =
        panel.querySelector('.bst-featured-collections__panel-picture--desktop .bst-featured-collections__panel-image') ||
        panel.querySelector('.bst-featured-collections__panel-image');
      if (!img) return;

      const desktopSizes = i === this.activeIndex ? '437px' : '180px';
      img.sizes = `(min-width: 990px) ${desktopSizes}, ${mobileSizes}`;
    });
  }
}

if (!customElements.get('featured-collections-component')) {
  customElements.define('featured-collections-component', FeaturedCollectionsComponent);
}
